/**
 * Settings Subscription Card Component
 * Displays subscription status in settings with navigation to details screen
 */

import React, {useMemo, useState, useCallback} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ViewStyle,
  TextStyle,
  ActivityIndicator,
  Alert,
} from 'react-native';
import Animated, {FadeInDown} from 'react-native-reanimated';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius} from '~constants/design';
import {useNavigation} from '@react-navigation/native';
import {useFocusEffect} from '@react-navigation/native';
import {NavigationProps} from '~types/navigation/navigation.type';
import {SCREENS} from '~constants';
import {useGetActivePlan} from '~hooks/subscription/useGetActivePlan';

interface SettingsSubscriptionCardProps {
  delay?: number;
}

const SettingsSubscriptionCard: React.FC<SettingsSubscriptionCardProps> = ({
  delay = 250,
}) => {
  const {colors, themeMode} = useTheme();
  const navigation = useNavigation<NavigationProps>();
  const {data: activePlan, isLoading, refetch} = useGetActivePlan();

  // Refetch active plan when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  const styles = useMemo(() => {
    const containerStyle: ViewStyle = {
      marginHorizontal: spacing.md,
      marginVertical: spacing.md,
      borderRadius: borderRadius.xl,
      overflow: 'hidden',
    };

    const cardStyle: ViewStyle = {
      backgroundColor: colors.surface,
      padding: spacing.lg,
      borderWidth: 1,
      borderColor: colors.primary + '30',
    };

    const headerStyle: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: spacing.md,
    };

    const iconContainerStyle: ViewStyle = {
      width: 48,
      height: 48,
      borderRadius: borderRadius.lg,
      backgroundColor: colors.primary + '15',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: spacing.md,
    };

    const titleStyle: TextStyle = {
      fontSize: 16,
      fontWeight: '700',
      color: colors.text,
      marginBottom: spacing.xs,
    };

    const subtitleStyle: TextStyle = {
      fontSize: 13,
      color: colors.textSecondary,
    };

    const contentStyle: ViewStyle = {
    };

    const badgeContainerStyle: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: spacing.md,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      backgroundColor: colors.primary + '12',
      borderRadius: borderRadius.lg,
    };

    const badgeIconStyle: TextStyle = {
      fontSize: 14,
      marginRight: spacing.sm,
    };

    const badgeTextStyle: TextStyle = {
      fontSize: 12,
      fontWeight: '600',
      color: colors.primary,
    };

    const ctaButtonStyle: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      backgroundColor: colors.primary,
      borderRadius: borderRadius.lg,
    };

    const ctaButtonTextStyle: TextStyle = {
      fontSize: 14,
      fontWeight: '700',
      color: '#FFFFFF',
      marginLeft: spacing.sm,
    };

    const featureListStyle: ViewStyle = {
      marginBottom: spacing.md,
    };

    const featureItemStyle: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: spacing.xs,
    };

    const featureCheckStyle: TextStyle = {
      fontSize: 14,
      color: colors.primary,
      marginRight: spacing.sm,
      fontWeight: '700',
    };

    const featureTextStyle: TextStyle = {
      fontSize: 12,
      color: colors.text,
      flex: 1,
    };

    return {
      containerStyle,
      cardStyle,
      headerStyle,
      iconContainerStyle,
      titleStyle,
      subtitleStyle,
      contentStyle,
      badgeContainerStyle,
      badgeIconStyle,
      badgeTextStyle,
      ctaButtonStyle,
      ctaButtonTextStyle,
      featureListStyle,
      featureItemStyle,
      featureCheckStyle,
      featureTextStyle,
    };
  }, [colors, themeMode]);

  const handleNavigateToSubscriptions = () => {
    navigation.navigate(SCREENS.SUBSCRIPTIONS as never);
  };

  return (
    <Animated.View
      entering={FadeInDown.delay(delay).duration(400)}
      style={styles.containerStyle}>
      <View style={styles.cardStyle}>
        {/* Header with Icon */}
        <View style={styles.headerStyle}>
          <View style={styles.iconContainerStyle}>
            <Text style={{fontSize: 24}}>👑</Text>
          </View>
          <View style={{flex: 1}}>
            <Text style={styles.titleStyle}>
              {activePlan ? 'Active Subscription' : 'Monthly Subscriptions'}
            </Text>
            <Text style={styles.subtitleStyle}>
              {activePlan
                ? 'Enjoy your premium features'
                : 'Get access to unlimited Pyxi experiences'}
            </Text>
          </View>
        </View>

        {/* Display Active Plan Info if available */}
        {activePlan && (
          <View style={styles.contentStyle}>
            {/* Plan Name and Price */}
            <View style={styles.badgeContainerStyle}>
              <View style={{flex: 1}}>
                <Text style={[styles.badgeTextStyle, {fontSize: 13, fontWeight: '700', marginBottom: 2}]}>
                  {activePlan.plan.name}
                </Text>
                <Text style={[styles.badgeTextStyle, {fontSize: 11, fontWeight: '500', color: colors.textSecondary}]}>
                  £{activePlan.plan.price.toFixed(2)} / {activePlan.plan.interval}
                </Text>
              </View>
            </View>

            {/* Plan Features */}
            {activePlan.plan.feature_list.display_text && (
              <View style={styles.featureListStyle}>
                <Text style={[styles.featureTextStyle, {fontWeight: '600', marginBottom: spacing.sm}]}>
                  Includes:
                </Text>
                <View style={styles.featureItemStyle}>
                  <Text style={styles.featureCheckStyle}>✓</Text>
                  <Text style={styles.featureTextStyle}>
                    {activePlan.plan.feature_list.display_text}
                  </Text>
                </View>
              </View>
            )}

            {/* End Date Info */}
            {activePlan.end_date && (
              <View style={{paddingVertical: spacing.xs, paddingHorizontal: spacing.sm, backgroundColor: colors.primary + '08', borderRadius: borderRadius.md, marginBottom: spacing.md}}>
                <Text style={[styles.subtitleStyle, {fontSize: 11}]}>
                  Renews on: {new Date(activePlan.end_date).toLocaleDateString('en-GB')}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Loading State */}
        {isLoading && (
          <View style={{justifyContent: 'center', alignItems: 'center', paddingVertical: spacing.lg}}>
            <ActivityIndicator color={colors.primary} size="small" />
          </View>
        )}

        {/* CTA Button */}
        {!isLoading && (
          <TouchableOpacity
            style={styles.ctaButtonStyle}
            onPress={handleNavigateToSubscriptions}
            activeOpacity={0.85}>
            <Text style={{fontSize: 16}}>{activePlan ? '✨' : '🚀'}</Text>
            <Text style={styles.ctaButtonTextStyle}>
              {activePlan ? 'Upgrade / Change Plan' : 'View Plans'}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </Animated.View>
  );
};

export default SettingsSubscriptionCard;
